// GuestRoom — server-enforced waiting room for external guests who open a Team
// Room link without an admin session. The guest gets NO LiveKit token until a
// host admits them; until then they wait. Rendered full-screen, outside admin auth.
import React, { useEffect, useRef, useState } from 'react';
import '@livekit/components-styles';
import { LiveKitRoom, VideoConference, PreJoin, useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';

const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL || '';
const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const FILL_CSS = `
@keyframes sanoirePulseDot { 0%,100%{opacity:1} 50%{opacity:.35} }
.sanoire-lk { --lk-accent-bg:#314F5C; --lk-accent-fg:#E0F5FC; --lk-bg:#0b1620; --lk-fg:#eef5f9; --lk-border-color:rgba(255,255,255,0.08); --lk-control-bg:rgba(255,255,255,0.06); }
.sanoire-lk, .sanoire-lk .lk-room-container { width:100%; height:100%; }
.sanoire-lk .lk-video-conference { height:100%; }
.sanoire-lk .lk-prejoin { width:100%; height:100%; max-width:none; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; padding:20px; overflow-y:auto; }
.sanoire-lk .lk-prejoin .lk-video-container, .sanoire-lk .lk-prejoin video { width:100%; max-width:680px; flex:1; min-height:220px; max-height:46vh; border-radius:16px; object-fit:cover; background:#0b1620; }
.sanoire-lk .lk-prejoin .lk-join-button, .sanoire-lk .lk-prejoin button[type="submit"] { width:100%; max-width:680px; padding:14px; border-radius:12px; font-size:15px; font-weight:700; background:#314F5C; color:#E0F5FC; }
.sanoire-lk .lk-video-conference { position: relative; overflow: hidden; display: flex; flex-direction: row; }
.sanoire-lk .lk-video-conference > *:not(.lk-chat) { flex: 1 1 auto; min-width: 0; }
.sanoire-lk .lk-chat {
  position: static; inset: auto; width: auto; height: auto;
  flex: 0 0 clamp(280px, 30%, 360px); align-self: stretch; min-width: 0;
  background: #0f1f2b; border-left: 1px solid rgba(255,255,255,0.1);
  display: flex; flex-direction: column;
}
.sanoire-lk .lk-chat-header {
  padding: 14px 16px; font-size: 13.5px; font-weight: 700; letter-spacing: -.01em;
  color: #eef5f9; border-bottom: 1px solid rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: space-between;
}
.sanoire-lk .lk-chat-messages, .sanoire-lk .lk-chat ul.lk-list {
  flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; list-style: none; margin: 0;
}
.sanoire-lk .lk-chat-entry { font-size: 13px; line-height: 1.5; color: #d9e6ee; }
.sanoire-lk .lk-chat-entry .lk-meta-data, .sanoire-lk .lk-chat-entry .lk-participant-name { color: #AFE1F6; font-weight: 700; font-size: 11.5px; }
.sanoire-lk .lk-chat-entry .lk-message-body { background: rgba(255,255,255,0.05); border-radius: 10px; padding: 8px 11px; display: inline-block; margin-top: 3px; }
.sanoire-lk .lk-chat-form { padding: 12px; border-top: 1px solid rgba(255,255,255,0.08); gap: 8px; align-items: center; background: #0f1f2b; }
.sanoire-lk .lk-chat-form-input {
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px; color: #eef5f9; padding: 10px 12px; font-size: 13px; flex: 1; outline: none;
}
.sanoire-lk .lk-chat-form-input::placeholder { color: #6f8b9b; }
.sanoire-lk .lk-chat-form-button {
  background: #314F5C; color: #E0F5FC; border-radius: 10px; border: none; padding: 0 14px; height: 38px; font-weight: 600; cursor: pointer;
}
.sanoire-lk .lk-chat-form-button:hover { background: #3c5d6b; }
.sanoire-lk .lk-chat .lk-close-button { color: #9fb6c4; background: transparent; }
@media (max-width: 767px) {
  .sanoire-lk .lk-chat { position: absolute; left: 0; right: 0; bottom: 0; top: auto; height: 56%; max-height: 56%; width: 100%; max-width: none; z-index: 40; flex: none; border-left: none; border-top: 1px solid rgba(255,255,255,0.14); border-radius: 16px 16px 0 0; box-shadow: 0 -12px 34px rgba(0,0,0,0.5); }
}
`;


function ConnStatus() {
  const state = useConnectionState();
  if (state === ConnectionState.Connected) return null;
  const map = {};
  map[ConnectionState.Connecting] = { t: 'Connecting\u2026', c: '#AFE1F6' };
  map[ConnectionState.Reconnecting] = { t: 'Reconnecting\u2026', c: '#E6B400' };
  map[ConnectionState.SignalReconnecting] = { t: 'Reconnecting\u2026', c: '#E6B400' };
  map[ConnectionState.Disconnected] = { t: 'Connection lost \u2014 leave and rejoin if this persists', c: '#ef5350' };
  const s = map[state];
  if (!s) return null;
  return (
    <div style={{ position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 50, background: 'rgba(11,22,32,0.96)', color: s.c, border: '1px solid rgba(255,255,255,0.14)', borderRadius: 999, padding: '7px 16px', fontFamily: 'system-ui,-apple-system,sans-serif', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.4)' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.c, animation: 'sanoirePulseDot 1.2s ease-in-out infinite' }} />{s.t}
    </div>
  );
}

export default function GuestRoom({ room }) {
  const [phase, setPhase] = useState('form'); // form|waiting|admitted|denied|error|choices
  const [name, setName] = useState('');
  const [token, setToken] = useState(null);
  const [choices, setChoices] = useState(null);
  const [err, setErr] = useState('');
  const knockRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  async function ask() {
    const nm = name.trim();
    if (!nm) { setErr('Please enter your name'); return; }
    setErr(''); setPhase('waiting');
    try {
      const qs = new URLSearchParams({ room, name: nm }).toString();
      const r = await fetch('/api/livekit-token?' + qs); // no auth → guest knock
      const d = await r.json();
      if (d.token) { setToken(d.token); setPhase('admitted'); return; } // (admin opened in guest view)
      if (!d.knockId) { setErr(d.error || 'Could not request to join'); setPhase('error'); return; }
      knockRef.current = d.knockId;
      pollRef.current = setInterval(async () => {
        try {
          const sr = await fetch('/api/knock-status?knockId=' + encodeURIComponent(knockRef.current));
          const sd = await sr.json();
          if (sd.status === 'admitted' && sd.token) { clearInterval(pollRef.current); setToken(sd.token); setPhase('admitted'); }
          else if (sd.status === 'denied') { clearInterval(pollRef.current); setPhase('denied'); }
        } catch (_e) {}
      }, 2500);
    } catch (e) { setErr((e && e.message) || 'Network error'); setPhase('error'); }
  }

  const wrap = { position: 'fixed', inset: 0, background: 'linear-gradient(180deg,#0b1620 0%,#102530 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F, color: '#eef5f9', padding: 20, zIndex: 99999 };
  const card = { width: 'min(420px, 92vw)', textAlign: 'center' };

  if (!LIVEKIT_URL) return <div style={wrap}><div style={card}>This meeting link isn't active.</div></div>;

  if (phase === 'admitted' && token) {
    if (!choices) {
      return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }} data-lk-theme="default" className="sanoire-lk">
          <style>{FILL_CSS}</style>
          <div style={{ textAlign: 'center', padding: '20px 16px 4px', color: '#eef5f9', fontFamily: F }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>You're in — ready to join?</div>
            <div style={{ fontSize: '.8rem', color: '#8fa9b8', marginTop: 4 }}>Check your camera and mic.</div>
          </div>
          <PreJoin defaults={{ username: name || 'Guest', videoEnabled: true, audioEnabled: true }} onSubmit={(v) => setChoices(v)} joinLabel="Join meeting" />
        </div>
      );
    }
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }} data-lk-theme="default" className="sanoire-lk">
        <style>{FILL_CSS}</style>
        <LiveKitRoom serverUrl={LIVEKIT_URL} token={token} connect
          video={choices.videoEnabled} audio={choices.audioEnabled}
          onDisconnected={() => setPhase('left')} style={{ height: '100%', width: '100%', minWidth: 0 }}>
          <VideoConference />
          <ConnStatus />
        </LiveKitRoom>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-.02em', marginBottom: 6 }}>Sanoire Team Room</div>
        {phase === 'form' && (
          <>
            <div style={{ fontSize: '.85rem', color: '#8fa9b8', marginBottom: 20 }}>Enter your name to ask the host to let you in.</div>
            <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') ask(); }} placeholder="Your name" style={{ width: '100%', boxSizing: 'border-box', padding: '13px 14px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)', color: '#eef5f9', fontSize: 15, fontFamily: F, outline: 'none', marginBottom: 12 }} />
            <button onClick={ask} style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: '#314F5C', color: '#E0F5FC', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: F }}>Ask to join</button>
            {err && <div style={{ color: '#ef9a9a', fontSize: '.78rem', marginTop: 10 }}>{err}</div>}
          </>
        )}
        {phase === 'waiting' && (
          <>
            <div style={{ width: 44, height: 44, margin: '8px auto 16px', border: '3px solid rgba(255,255,255,0.18)', borderTopColor: '#AFE1F6', borderRadius: '50%', animation: 'gspin 0.9s linear infinite' }} />
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>Waiting for the host to let you in…</div>
            <div style={{ fontSize: '.8rem', color: '#8fa9b8', marginTop: 6 }}>Hang tight, {name}. You'll join automatically.</div>
            <style>{`@keyframes gspin{to{transform:rotate(360deg)}}`}</style>
          </>
        )}
        {phase === 'denied' && <div style={{ fontSize: '1rem', color: '#ef9a9a' }}>The host didn't admit you to this meeting.</div>}
        {phase === 'left' && <div style={{ fontSize: '1rem' }}>You've left the meeting.</div>}
        {phase === 'error' && <div style={{ fontSize: '.95rem', color: '#ef9a9a' }}>{err || 'Something went wrong.'}</div>}
      </div>
    </div>
  );
}
